import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { Money, Qty } from '../../../components/Money';
import { DateRangeControl } from '../components/DateRangeControl';
import { Segmented } from '../components/ui';
import type { ReportParams } from '../components/useReportParams';
import { INTERVALS, type Interval } from '../lib/dateRange';

/** The standard filter bar: date range plus (optionally) the interval selector. */
export function RangeControls({ p, interval }: { p: ReportParams; interval?: boolean }) {
  return (
    <>
      <DateRangeControl
        range={p.range}
        preset={p.preset}
        onPreset={p.setPreset}
        onCustom={p.setCustom}
      />
      {interval && (
        <Segmented<Interval>
          label="Interval"
          value={p.interval}
          options={INTERVALS}
          onChange={p.setInterval}
        />
      )}
    </>
  );
}

/** Product name with SKU underneath. */
export function ItemCell({ name, sku }: { name: string; sku?: string | null }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {name}
      </Typography>
      {sku && (
        <Typography variant="caption" color="text.secondary">
          {sku}
        </Typography>
      )}
    </Box>
  );
}

export function M({
  v,
  strong,
  signed,
}: {
  v: string | number | null | undefined;
  strong?: boolean;
  signed?: boolean;
}) {
  return <Money value={v} strong={strong} signed={signed} />;
}

export function Q({ v, unit }: { v: string | number | null | undefined; unit?: string }) {
  return <Qty value={v} unit={unit} />;
}

/** A two-column layout for chart + side panel that stacks on small screens. */
export function ChartRow({
  children,
  ratio = '1.7fr 1fr',
}: {
  children: ReactNode;
  ratio?: string;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2.5,
        gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: ratio },
        mb: 2.5,
        alignItems: 'start',
        '& > *': { minWidth: 0 },
      }}
    >
      {children}
    </Box>
  );
}

/** Margin percentage as a coloured figure. */
export function Margin({ value }: { value: string | null | undefined }) {
  if (value === null || value === undefined) return <>—</>;
  const n = Number(value);
  return (
    <Box
      component="span"
      className="tabular"
      sx={{
        fontWeight: 600,
        color: n < 0 ? 'error.main' : n >= 25 ? 'success.main' : 'text.primary',
      }}
    >
      {n.toFixed(1)}%
    </Box>
  );
}
